// Adaptive Exam Service
// Pure JavaScript — NO OpenAI calls.
// Selects 50 questions from the pre-written question bank,
// weighted by user weakness (lowest accuracy topics get more questions).

import type { Question } from '../types';
import { questionMapper } from '../question_data/questionMapper';
import { INSIGHT_TOPICS, HAZARD_TOPICS, KNOWLEDGE_TOPICS } from '../question_data/categoryStructure';
import {
  getAllTopicPerformance,
  getExamMeta,
  recordExamMilestone,
  type TopicPerformance
} from './topicPerformanceService';
import { logger } from '../utils/logger';

// ─── Constants ───────────────────────────────────

const EXAM_TOTAL_QUESTIONS = 50;
const WEAK_TOPIC_QUESTION_SHARE = 0.6;   // 60% from weak topics
const BALANCED_QUESTION_SHARE = 0.3;     // 30% balanced spread
const _ARCHIVE_QUESTION_SHARE = 0.1;      // 10% from archive/unseen

// All active topic IDs (camelCase, matching question bank)
const ALL_ACTIVE_TOPICS: string[] = [
  ...INSIGHT_TOPICS,
  ...HAZARD_TOPICS,
  ...KNOWLEDGE_TOPICS
];

// ─── Core algorithm ──────────────────────────────

/**
 * Generates a 50-question adaptive exam from the existing question bank.
 * Runs in milliseconds. Zero API cost. Zero wait time.
 *
 * Weighting strategy:
 *   - 60% from the user's weakest topics (lowest accuracy)
 *   - 30% balanced across all topics the user has practiced
 *   - 10% from archive / topics the user hasn't practiced yet
 *
 * Avoids repeating questions from the last exam (stored in user_exam_meta).
 */
export function generateAdaptiveExam(
  performanceRows: TopicPerformance[],
  previousQuestionIds: string[]
): Question[] {
  const mapping = questionMapper.getMapping();
  const previousSet = new Set(previousQuestionIds);

  // Build a pool of all available questions keyed by topic
  const topicQuestionPool = new Map<string, Question[]>();

  const addToPool = (category: Map<string, Question[]>) => {
    category.forEach((questions, topicId) => {
      const existing = topicQuestionPool.get(topicId) || [];
      topicQuestionPool.set(topicId, [...existing, ...questions]);
    });
  };

  addToPool(mapping.insight);
  addToPool(mapping.hazardPerception);
  addToPool(mapping.knowledge);

  // Sort performance by accuracy ASC (weakest first)
  const sorted = [...performanceRows].sort((a, b) => a.accuracy - b.accuracy);
  const practicedTopicIds = new Set(sorted.map(r => r.topic_id));
  const unpracticedTopicIds = ALL_ACTIVE_TOPICS.filter(t => !practicedTopicIds.has(t));

  // ── Weak-topic questions (60%) ──
  const weakCount = Math.floor(EXAM_TOTAL_QUESTIONS * WEAK_TOPIC_QUESTION_SHARE);
  const weakTopics = sorted.slice(0, Math.max(3, Math.ceil(sorted.length / 2)));
  const weakQuestions = selectFromTopics(
    weakTopics.map(t => t.topic_id),
    weakCount,
    topicQuestionPool,
    previousSet
  );

  // ── Balanced questions (30%) ──
  const balancedCount = Math.floor(EXAM_TOTAL_QUESTIONS * BALANCED_QUESTION_SHARE);
  const balancedTopics = sorted.map(t => t.topic_id);
  const alreadyUsedIds = new Set(weakQuestions.map(q => q.id));
  const balancedQuestions = selectFromTopics(
    balancedTopics,
    balancedCount,
    topicQuestionPool,
    new Set([...Array.from(previousSet), ...Array.from(alreadyUsedIds)])
  );

  // ── Archive / unpracticed questions (10%) ──
  const archiveCount = EXAM_TOTAL_QUESTIONS - weakQuestions.length - balancedQuestions.length;
  const allUsedIds = new Set([
    ...Array.from(previousSet),
    ...weakQuestions.map(q => q.id),
    ...balancedQuestions.map(q => q.id)
  ]);

  let archiveQuestions: Question[] = [];
  if (unpracticedTopicIds.length > 0) {
    archiveQuestions = selectFromTopics(
      unpracticedTopicIds,
      archiveCount,
      topicQuestionPool,
      allUsedIds
    );
  }

  // If not enough from unpracticed, backfill from archive pool
  if (archiveQuestions.length < archiveCount) {
    const remaining = archiveCount - archiveQuestions.length;
    const archivePool: Question[] = [];
    mapping.archive.forEach(questions => archivePool.push(...questions));
    const backfillUsed = new Set([...Array.from(allUsedIds), ...archiveQuestions.map(q => q.id)]);
    const filtered = archivePool.filter(q => !backfillUsed.has(q.id));
    archiveQuestions.push(...shuffle(filtered).slice(0, remaining));
  }

  const exam = [...weakQuestions, ...balancedQuestions, ...archiveQuestions];
  return shuffle(exam).slice(0, EXAM_TOTAL_QUESTIONS);
}

// ─── Helpers ─────────────────────────────────────

function selectFromTopics(
  topicIds: string[],
  totalCount: number,
  pool: Map<string, Question[]>,
  excludeIds: Set<string>
): Question[] {
  if (topicIds.length === 0) return [];

  const perTopic = Math.max(1, Math.floor(totalCount / topicIds.length));
  const selected: Question[] = [];

  for (const topicId of topicIds) {
    const available = (pool.get(topicId) || []).filter(q => !excludeIds.has(q.id));
    const picked = shuffle(available).slice(0, perTopic);
    selected.push(...picked);
    picked.forEach(q => excludeIds.add(q.id));
  }

  // If we still need more (rounding), take from the first topics with remaining questions
  if (selected.length < totalCount) {
    for (const topicId of topicIds) {
      if (selected.length >= totalCount) break;
      const available = (pool.get(topicId) || []).filter(q => !excludeIds.has(q.id));
      const extra = shuffle(available).slice(0, totalCount - selected.length);
      selected.push(...extra);
      extra.forEach(q => excludeIds.add(q.id));
    }
  }

  return selected.slice(0, totalCount);
}

function shuffle<T>(array: T[]): T[] {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Orchestrator (called from PracticeTest on 5th test) ─

/**
 * Full pipeline: reads DB → generates exam → stores question IDs in DB.
 * Called from handlePracticeComplete when completedTests % 5 === 0.
 */
export async function generateAndStoreAdaptiveExam(
  userId: string,
  milestone: number
): Promise<Question[] | null> {
  try {
    const [performance, meta] = await Promise.all([
      getAllTopicPerformance(userId),
      getExamMeta(userId)
    ]);

    if (performance.length === 0) {
      logger.info('No topic performance data yet — skipping exam generation');
      return null;
    }

    const exam = generateAdaptiveExam(performance, meta.last_exam_question_ids);

    if (exam.length === 0) {
      logger.warn('Adaptive exam generated 0 questions');
      return null;
    }

    // Store milestone and question IDs so next exam avoids repeats
    const questionIds = exam.map(q => q.id);
    await recordExamMilestone(userId, milestone, questionIds);

    logger.info(`Adaptive exam generated: ${exam.length} questions at milestone ${milestone}`);
    return exam;
  } catch (error) {
    logger.error('generateAndStoreAdaptiveExam error:', error);
    return null;
  }
}
